import { trapFocus, removeTrapFocus, inertOutside } from '@theme/focus';
import { whenUpcartReady } from '@theme/upcart-runtime';

/**
 * Modal focus behavior and dialog semantics for the UpCart cart drawer.
 */

// The `styles_*` classes beside these are build-hashed; only the `upcart-` names are safe to hook.
const PANEL_SELECTOR = '.upcart-internal-cart-preview__container';
const HEADING_SELECTOR = '.upcart-internal-header h3';
const HEADING_ID = 'upcart-a11y-title';

/** @type {HTMLElement | null} */
let triggerElement = null;

/** @type {(() => void) | null} */
let undoInert = null;

let isOpen = false;

/**
 * @param {ShadowRoot} shadowRoot
 * @returns {HTMLElement | null} the panel, so the caller can move focus onto it
 */
function applyDialogSemantics(shadowRoot) {
  const panel = shadowRoot.querySelector(PANEL_SELECTOR);
  if (!panel) return null;

  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');

  // A screen reader announces the dialog when focus lands on the dialog element. Landing on a
  // control inside it announces only that control. -1 keeps the panel out of the tab ring.
  panel.setAttribute('tabindex', '-1');

  const heading = shadowRoot.querySelector(HEADING_SELECTOR);
  if (heading) {
    heading.id = HEADING_ID;
    panel.setAttribute('aria-labelledby', HEADING_ID);
  } else {
    panel.setAttribute('aria-label', 'Shopping cart');
  }

  return panel;
}

/**
 * The panel stays in the DOM when closed, so a stale role="dialog" would leave a phantom modal.
 *
 * @param {ShadowRoot} shadowRoot
 */
function clearDialogSemantics(shadowRoot) {
  const panel = shadowRoot.querySelector(PANEL_SELECTOR);
  if (!panel) return;

  for (const attr of ['role', 'aria-modal', 'aria-labelledby', 'aria-label', 'tabindex']) panel.removeAttribute(attr);
}

const isApiReady = () =>
  window.upcartDocumentOrShadowRoot instanceof ShadowRoot &&
  typeof window.upcartSubscribeCartOpened === 'function' &&
  typeof window.upcartSubscribeCartClosed === 'function';

/**
 * @param {boolean} restoreFocus
 */
function teardownModal(restoreFocus) {
  isOpen = false;
  undoInert?.();
  undoInert = null;

  const shadowRoot = window.upcartDocumentOrShadowRoot;
  if (shadowRoot instanceof ShadowRoot) {
    shadowRoot.host.inert = true;
    clearDialogSemantics(shadowRoot);
  }

  removeTrapFocus(restoreFocus && triggerElement?.isConnected ? triggerElement : null);
}

function init() {
  const { host } = window.upcartDocumentOrShadowRoot;

  // The closed drawer stays on-screen at full size, just translated out of view, so every control in
  // it is still tabbable. `inert` reaches into the shadow root and covers the whole flat subtree.
  host.inert = true;

  // Focus is already inside the shadow root by the time cartOpened fires, so track the trigger here.
  document.addEventListener(
    'focusin',
    ({ target }) => {
      if (target instanceof HTMLElement && !host.contains(target) && target !== host) triggerElement = target;
    },
    true
  );

  window.upcartSubscribeCartOpened(() => {
    isOpen = true;
    host.inert = false;
    undoInert?.();
    // Only isolate the page when the drawer has somewhere for the keyboard to land. An empty trap
    // still installs inertOutside, which would lock the storefront with no tabbable escape hatch.
    const shadowRoot = window.upcartDocumentOrShadowRoot;
    const hasFocusable = Boolean(
      shadowRoot.querySelector(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    );
    undoInert = hasFocusable ? inertOutside(host) : null;
    const panel = applyDialogSemantics(shadowRoot);
    // trapFocus enters a ShadowRoot at its first tabbable, which would skip the dialog.
    trapFocus(shadowRoot);
    panel?.focus();
  });

  window.upcartSubscribeCartClosed(() => {
    teardownModal(true);
  });

  // UpCart replaces its whole subtree on every cart update, discarding the attributes above.
  window.upcartSubscribeCartUpdated?.(() => {
    if (isOpen) applyDialogSemantics(window.upcartDocumentOrShadowRoot);
  });

  // Leaving for checkout doesn't close the drawer, so Back restores it open from the bfcache. Close
  // it through UpCart rather than tearing down here: making the host inert while UpCart still shows
  // the drawer leaves a visible cart that ignores every click.
  window.addEventListener('pageshow', ({ persisted }) => {
    if (persisted && isOpen) window.upcartCloseCart?.();
  });
}

whenUpcartReady(
  isApiReady,
  init,
  '[upcart-a11y] UpCart runtime hooks never appeared; its cart drawer has no focus trap or focus restore. ' +
    'Check whether the app changed its API (upcartDocumentOrShadowRoot / upcartSubscribeCartOpened / upcartSubscribeCartClosed).'
);
